import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Drawer,
  IconButton,
  useTheme,
  CircularProgress,
  TextField,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import { AiGradientButton } from '../shared';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import RoomsStep from './steps/RoomsStep';
import GuestDetailsStep from './steps/GuestDetailsStep';
import ReviewStep from './steps/ReviewStep';
import WalkInAiCheckinOverlay from './WalkInAiCheckinOverlay';
import BookingConfirmedActionsContent from './booking/BookingConfirmedActionsContent';
import CompanyDetailsHeadingActions from './booking/CompanyDetailsHeadingActions';
import { computeAmountDueForPaymentLink } from '../utils/bookingPaymentLink';
import { getBookingCurrencySymbol } from '../utils/bookingPaymentSummary';
import { useBookingPaymentLinkComm } from '../hooks/useBookingPaymentLinkComm';
import {
  getAllRooms,
  submitWalkIn,
  getTaxRulesAvailability,
  downloadBookingInvoice,
  downloadBookingConfirmation,
  sendBookingConfirmation,
  searchGuestByPhone,
  getCustomerDetail,
  getRoomCleanings,
} from '../services/api';
import { formatWhatsAppSuccessMessage } from '../utils/whatsappMessaging';
import countriesData from '../data/countries-states-cities-data';
import countryCodesData from '../data/country-codes.json';
import DashboardHeader from './common/DashboardHeader';
import WalkInMobileStepBar from './booking/WalkInMobileStepBar';
import DashboardHousekeepingDrawer from './dashboard/DashboardHousekeepingDrawer';
import AlertDialog from './common/AlertDialog';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { resolveDashboardThemeMode } from '../utils/dashboardSurfaceSx';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import { PERMISSIONS } from '../auth/permissions';
import usePermission from '../auth/usePermission';
import {
  getWalkInFooterButtonSx,
  getWalkInFooterSx,
  getWalkInFormPaperSx,
  getWalkInHeadingActionsSx,
  getWalkInOuterPaperSx,
  getWalkInRoomsStepBodySx,
  getWalkInRoomsStepGridItemSx,
  getWalkInRoomsStepPaperSx,
  getWalkInShellSx,
  isWalkInFlowLayout,
  scrollMobileStepTopIntoView,
} from '../utils/walkInMobileLayout';
import { readStoredWalkInGroupView, writeStoredWalkInGroupView } from '../utils/roomUtils';
import { buildPendingCleaningByRoomId } from '../utils/dashboardPendingTasks';
import { resolveWalkInHousekeepingCleaningId, patchWalkInRoomCleanStatus } from '../utils/walkInHousekeeping';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../utils/locationSearch';
import { normalizeDocumentIdForSubmit, normalizeDocumentIdInput } from '../utils/documentIdFormatters';
import { isValidPhoneForCountry } from '../utils/phoneValidation';
import { calculateBookingPriceBreakdown } from '../utils/bookingPriceCalculation';
import { isAutoGstEnabled } from '../utils/autoGst';
import { toPaymentProcessedAtIso } from '../utils/dateUtils';
import {
  EMPTY_FOREIGN_GUEST_DETAILS,
  foreignDetailsToGuestApiPayload,
  isForeignGuestCountry,
} from '../utils/foreignGuestFormUtils';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  createBookingDrawerOverlayProps,
} from '../utils/bookingDrawerLayout';

const VALID_DIAL_CODES = new Set(countryCodesData.map((cc) => cc.dialCode));
const EMPTY_COMPANY_DETAILS = {
  taxIdType: 'gstin',
  taxIdNumber: '',
  companyName: '',
  companyPhone: '',
  companyEmail: '',
  companyAddress: '',
  companyCity: '',
  companyState: '',
  companyCountry: '',
  companyPincode: '',
};

const hasAnyCompanyFieldValues = (details) =>
  Boolean(
    details &&
      (
        details.taxIdNumber ||
        details.companyName ||
        details.companyPhone ||
        details.companyEmail ||
        details.companyAddress ||
        details.companyCity ||
        details.companyState ||
        details.companyCountry ||
        details.companyPincode
      )
  );

const isCompanyFormEffectivelyEmpty = (details) => !hasAnyCompanyFieldValues(details);

const STEPS = {
  ROOMS: 0,
  GUEST_DETAILS: 1,
  REVIEW: 2,
};

const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const randomNibble = Math.floor(Math.random() * 16);
    const value = char === 'x' ? randomNibble : (randomNibble & 0x3) | 0x8;
    return value.toString(16);
  });
};

const toCurrencyAmount = (value) => Math.round((Number(value) || 0) * 100) / 100;
const getSelectedHotelCurrency = () =>
  (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel?.currency) || 'INR';

// GST rate will be fetched from tax rules, default to 0 if no rules exist

const WalkInForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canManageAiCheckin = can(PERMISSIONS.AI_CHECKIN_MANAGE);
  const isMobileLayout = isMobileShellLayout();
  const useWalkInFlowLayout = isWalkInFlowLayout();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = resolveDashboardThemeMode(currentTheme) === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const selectedCurrency = getSelectedHotelCurrency();
  const selectedCurrencySymbol = getBookingCurrencySymbol(selectedCurrency);
  const companyDrawerOverlays = useMemo(
    () => createBookingDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );
  const {
    canSendPaymentLink,
    canSendPaymentLinkEmail,
    canSendPaymentLinkWhatsApp,
  } = useBookingPaymentLinkComm();
  const [activeStep, setActiveStep] = useState(STEPS.ROOMS);
  const [roomGroupView, setRoomGroupView] = useState(() => readStoredWalkInGroupView('room_type'));
  const [housekeepingCleaningId, setHousekeepingCleaningId] = useState(null);
  const [housekeepingError, setHousekeepingError] = useState('');
  const [pendingCleaningByRoomId, setPendingCleaningByRoomId] = useState({});
  const [housekeepingResolving, setHousekeepingResolving] = useState(false);
  // Walk-in always uses current date - no date selection
  const [checkInDate, setCheckInDate] = useState(moment().startOf('day'));
  const [checkOutDate, setCheckOutDate] = useState(moment().add(1, 'days').startOf('day'));
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [allRoomsData, setAllRoomsData] = useState(null);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Guest details - Name fields
  const [guestTitle, setGuestTitle] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestMiddleName, setGuestMiddleName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  // Guest details - Contact
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestPhoneCountryCode, setGuestPhoneCountryCode] = useState('+91');
  const [guestAlternatePhone, setGuestAlternatePhone] = useState('');
  const [guestAlternatePhoneCountryCode, setGuestAlternatePhoneCountryCode] = useState('+91');

  const handleGuestPhoneCountryCodeChange = useCallback((newCode) => {
    if (VALID_DIAL_CODES.has(newCode)) setGuestPhoneCountryCode(newCode);
  }, []);
  const handleGuestAlternatePhoneCountryCodeChange = useCallback((newCode) => {
    if (VALID_DIAL_CODES.has(newCode)) setGuestAlternatePhoneCountryCode(newCode);
  }, []);

  // Guest details - Address
  const [guestAddress, setGuestAddress] = useState('');
  const [guestCity, setGuestCity] = useState('');
  const [guestState, setGuestState] = useState('');
  const [guestCountry, setGuestCountry] = useState('');
  const [guestPincode, setGuestPincode] = useState('');
  // Guest details - Personal
  const [guestDOB, setGuestDOB] = useState('');
  const [guestGender, setGuestGender] = useState('');
  // Guest details - Other
  const [guestETA, setGuestETA] = useState('');
  const [guestNote, setGuestNote] = useState('');
  const [bookingReference, setBookingReference] = useState('');
  const [remarks, setRemarks] = useState('');
  // Guest details - Documents
  const [uploadDocumentType, setUploadDocumentType] = useState('no_document');
  const [documentIdNumber, setDocumentIdNumber] = useState('');
  const [passportFrontId, setPassportFrontId] = useState('');
  const [passportBackId, setPassportBackId] = useState('');
  const [purposeOfStay, setPurposeOfStay] = useState('');
  const [purposeOfStayNote, setPurposeOfStayNote] = useState('');
  const [foreignGuestDetails, setForeignGuestDetails] = useState(EMPTY_FOREIGN_GUEST_DETAILS);
  const hotelCountry =
    (typeof window !== 'undefined' &&
      ((window.DJANGO_CONTEXT || window.djangoContext)?.selectedHotel?.country ||
        (window.DJANGO_CONTEXT || window.djangoContext)?.hotel?.country)) ||
    '';

  // Booking type: 'nightly' or 'hourly'
  const [bookingType, setBookingType] = useState('nightly');

  // Review settings
  const autoGstEnabled = isAutoGstEnabled();
  const [gstIncluded, setGstIncluded] = useState(false);
  const effectiveGstIncluded = autoGstEnabled || gstIncluded;
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [allowDiscount, setAllowDiscount] = useState(false);
  const [discountMode, setDiscountMode] = useState('percentage');
  const [discountPercentage, setDiscountPercentage] = useState(5);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [allowExtraCharges, setAllowExtraCharges] = useState(false);
  const [extraChargesAmount, setExtraChargesAmount] = useState(0);
  const [extraChargesNote, setExtraChargesNote] = useState('');
  const [extraChargesItems, setExtraChargesItems] = useState([]);
  const [extraChargesBaseAmount, setExtraChargesBaseAmount] = useState(0);
  const [extraChargesGstAmount, setExtraChargesGstAmount] = useState(0);
  const [hasTaxRules, setHasTaxRules] = useState(false);
  const [gstPercentage, setGstPercentage] = useState(0);
  const [taxRules, setTaxRules] = useState([]);

  // Payment settings (for walk-in)
  const [paymentType, setPaymentType] = useState('full'); // Default to full payment
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('CASH'); // Default to CASH
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentDocument, setPaymentDocument] = useState('');
  const [collectionDate, setCollectionDate] = useState(() => moment());

  // Success dialog state
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [successActionLoading, setSuccessActionLoading] = useState(null);
  const [successPaymentLinkBookingId, setSuccessPaymentLinkBookingId] = useState(0);
  const [successPaymentLinkAmount, setSuccessPaymentLinkAmount] = useState(0);
  const [showPaymentLinkOnSuccess, setShowPaymentLinkOnSuccess] = useState(false);

  // Days selection for walk-in (default 1 night)
  const [selectedDays, setSelectedDays] = useState(1);

  // Error dialog state
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Alert dialog state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Alert');
  const [alertSeverity, setAlertSeverity] = useState('info');
  
  // Guest details step validation state
  const [isGuestDetailsValid, setIsGuestDetailsValid] = useState(false);
  const [selectedGuestId, setSelectedGuestId] = useState(null);
  const validateGuestDetailsRef = useRef(null);
  const submitIdempotencyKeyRef = useRef(null);
  const walkInShellRef = useRef(null);
  const guestDetailsTopRef = useRef(null);

  // Room drawer state
  const [roomDrawerOpen, setRoomDrawerOpen] = useState(false);
  const [roomFormData, setRoomFormData] = useState({
    roomTypeId: '',
    roomId: '',
    roomNumber: '',
    mealPlan: 'EP',
    days: 1,
    adults: 0,
    children: 0,
    ratePerNight: 0,
    extraAdultCharge: 0,
    extraChildCharge: 0,
  });

  // Corporate/company details (for corporate walk-in source)
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(EMPTY_COMPANY_DETAILS);
  const [companyDetailsSaved, setCompanyDetailsSaved] = useState(false);
  const [companyErrors, setCompanyErrors] = useState({});
  const [aiCheckinOpen, setAiCheckinOpen] = useState(false);
  const companyAutofillRequestRef = useRef(0);
  /** Company data from guest profile — applied only when user opens "Add Company Details". */
  const [guestProfileCompanySnapshot, setGuestProfileCompanySnapshot] = useState(null);
  const companyDetailsSavedRef = useRef(false);

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    companyDetailsSavedRef.current = companyDetailsSaved;
  }, [companyDetailsSaved]);

  const successPaymentLinkBooking = useMemo(
    () => ({
      guest_email: guestEmail || '',
      guest_phone: `${String(guestPhoneCountryCode || '').replace(/\s/g, '')}${String(guestPhone || '').replace(/\D/g, '')}`,
      guest_name: [guestTitle, guestFirstName, guestMiddleName, guestLastName].filter(Boolean).join(' ').trim(),
    }),
    [
      guestEmail,
      guestPhone,
      guestPhoneCountryCode,
      guestTitle,
      guestFirstName,
      guestMiddleName,
      guestLastName,
    ]
  );

  // Calculate nights from selected days
  const nights = selectedDays;
  
  // Handle days change - updates check-out date based on selected days
  // Check-in is always today for walk-in
  const handleDaysChange = (newDays) => {
    const validDays = Math.max(1, Math.min(30, newDays));
    setSelectedDays(validDays);
    // Update check-out date: today + validDays
    const today = moment().startOf('day');
    const checkout = today.clone().add(validDays, 'days');
    setCheckInDate(today); // Always ensure check-in is today
    setCheckOutDate(checkout);
  };
  
  // Initialize dates on mount - check-in is today, check-out is today + selectedDays
  useEffect(() => {
    const today = moment().startOf('day');
    const checkout = today.clone().add(selectedDays, 'days');
    setCheckInDate(today);
    setCheckOutDate(checkout);
  }, []); // Only run on mount
  
  // Keep check-in date as today when selectedDays changes
  useEffect(() => {
    const today = moment().startOf('day');
    const checkout = today.clone().add(selectedDays, 'days');
    setCheckInDate(today);
    setCheckOutDate(checkout);
  }, [selectedDays]);

  // Fetch all rooms - now accepts date range for availability filtering
  const fetchAllRooms = useCallback(async (checkIn, checkOut) => {
    setLoadingRooms(true);
    try {
      const params = { walk_in: '1' };
      if (checkIn && checkOut) {
        params.check_in_date = moment(checkIn).format('YYYY-MM-DD');
        params.check_out_date = moment(checkOut).format('YYYY-MM-DD');
      }
      const response = await getAllRooms(params);
      setAllRoomsData(response);
    } catch (error) {} finally {
      setLoadingRooms(false);
    }
  }, []);

  useEffect(() => {
    fetchAllRooms(checkInDate, checkOutDate);
  }, [fetchAllRooms, checkInDate, checkOutDate]);

  const loadPendingCleanings = useCallback(async () => {
    try {
      const response = await getRoomCleanings({ status: 'PENDING', page_size: 500 });
      setPendingCleaningByRoomId(buildPendingCleaningByRoomId(response));
    } catch {
      setPendingCleaningByRoomId({});
    }
  }, []);

  useEffect(() => {
    if (activeStep === STEPS.ROOMS) {
      loadPendingCleanings();
    }
  }, [activeStep, loadPendingCleanings]);

  // Fetch tax rules availability on component mount
  useEffect(() => {
    const fetchTaxRulesAvailability = async () => {
      try {
        const data = await getTaxRulesAvailability('INR');
        setHasTaxRules(data.has_tax_rules || false);
        setTaxRules(Array.isArray(data.tax_rules) ? data.tax_rules : []);
        if (data.tax_percentage) {
          setGstPercentage(data.tax_percentage);
        }
      } catch (error) {setHasTaxRules(false);
        setGstPercentage(0);
        setTaxRules([]);
      }
    };
    fetchTaxRulesAvailability();
  }, []);

  // Auto-populate country from hotel context
  useEffect(() => {
    const djangoContext = window.DJANGO_CONTEXT || {};
    const selectedHotel = djangoContext.selectedHotel || null;
    if (selectedHotel && selectedHotel.country) {
      // Hotel country might be a code (e.g., "IN") or name (e.g., "India")
      // Convert to country name if it's a code
      const hotelCountryValue = selectedHotel.country.trim();
      let countryName = hotelCountryValue;
      
      // Check if it's a country code or name by looking it up in countries data
      // Try exact match first (case-sensitive)
      let countryObj = countriesData.countries.find(c => 
        c.code === hotelCountryValue || c.name === hotelCountryValue
      );
      
      // If not found, try case-insensitive match
      if (!countryObj) {
        countryObj = countriesData.countries.find(c => 
          c.code?.toLowerCase() === hotelCountryValue.toLowerCase() || 
          c.name?.toLowerCase() === hotelCountryValue.toLowerCase()
        );
      }
      
      if (countryObj && countryObj.name) {
        // Use the full country name
        countryName = countryObj.name;
      }
      
      setGuestCountry(countryName);
    }
  }, []);

  // Look up guest by phone: store company data as snapshot only (do not auto-apply to this booking).
  useEffect(() => {
    const phoneDigits = (guestPhone || '').replace(/\D/g, '');
    if (!isValidPhoneForCountry(phoneDigits, guestPhoneCountryCode)) {
      companyAutofillRequestRef.current += 1;
      if (!companyDetailsSavedRef.current) {
        setCompanyDetails({ ...EMPTY_COMPANY_DETAILS });
        setCompanyDetailsSaved(false);
        setCompanyErrors({});
      }
      setGuestProfileCompanySnapshot(null);
      return undefined;
    }

    const requestId = ++companyAutofillRequestRef.current;
    const timer = setTimeout(async () => {
      try {
        const searchResult = await searchGuestByPhone(phoneDigits);
        if (companyAutofillRequestRef.current !== requestId) return;

        const guestId = Number(searchResult?.guest?.id);
        if (!Number.isInteger(guestId) || guestId <= 0) {
          setGuestProfileCompanySnapshot(null);
          if (!companyDetailsSavedRef.current) {
            setCompanyDetails({ ...EMPTY_COMPANY_DETAILS });
            setCompanyDetailsSaved(false);
            setCompanyErrors({});
          }
          return;
        }

        const customerDetail = await getCustomerDetail(guestId, 1, 1);
        if (companyAutofillRequestRef.current !== requestId) return;

        const customer = customerDetail?.customer || {};
        const normalizedTaxType = String(customer.company_tax_id_type || '').trim().toLowerCase();
        const taxIdType = ['gstin', 'pan', 'tan', 'cin', 'other'].includes(normalizedTaxType)
          ? normalizedTaxType
          : 'gstin';

        const nextCompanyDetails = {
          taxIdType,
          taxIdNumber: String(customer.company_tax_id_number || '').trim(),
          companyName: String(customer.company_name || '').trim(),
          companyPhone: String(customer.company_phone || '').trim(),
          companyEmail: String(customer.company_email || '').trim(),
          companyAddress: String(customer.company_address || '').trim(),
          companyCity: String(customer.company_city || '').trim(),
          companyState: String(customer.company_state || '').trim(),
          companyCountry: String(customer.company_country || '').trim(),
          companyPincode: String(
            customer.company_pincode || customer.company_postal_code || ''
          ).trim(),
        };

        const hasAnyCompanyValue = hasAnyCompanyFieldValues(nextCompanyDetails);

        setGuestProfileCompanySnapshot(hasAnyCompanyValue ? nextCompanyDetails : null);

        if (!companyDetailsSavedRef.current) {
          setCompanyDetails({ ...EMPTY_COMPANY_DETAILS });
          setCompanyDetailsSaved(false);
          setCompanyErrors({});
        }
      } catch (error) {
        // Keep the form usable if autofill request fails.
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [guestPhone, guestPhoneCountryCode]);

  // Calculate price breakdown
  // For walk-in: recalculate prices dynamically based on current nights (selectedDays)
  // This ensures prices update when user changes nights/days selection
  const priceBreakdown = React.useMemo(() => {
    let totalBasePrice = 0;
    let totalExtraAdultCharge = 0;
    let totalExtraChildCharge = 0;
    let totalRoomBasePrice = 0;

    // Use current nights (selectedDays) for walk-in calculations
    const currentNights = nights || 1;

    selectedRooms.forEach((room) => {
      // For walk-in: recalculate prices based on current nights instead of stored values
      // This allows prices to update when user changes nights/days selection
      const ratePerNight = room.ratePerNight || 0;
      const extraAdultRate = room.extraAdultRate || 0; // Rate per extra adult per night
      const extraChildRate = room.extraChildRate || 0; // Rate per extra child per night
      const extraAdultsCount = room.extraAdultsCount || 0;
      const extraChildrenCount = room.extraChildrenCount || 0;
      
      // Recalculate base price: ratePerNight * current nights
      const basePrice = ratePerNight * currentNights;
      
      // Recalculate extra charges: rate * count * current nights
      const extraAdultCharge = extraAdultRate * extraAdultsCount * currentNights;
      const extraChildCharge = extraChildRate * extraChildrenCount * currentNights;
      
      totalExtraAdultCharge += extraAdultCharge;
      totalExtraChildCharge += extraChildCharge;
      totalRoomBasePrice += basePrice;
      const extraCharges = extraAdultCharge + extraChildCharge;
      totalBasePrice += basePrice + extraCharges;
    });

    const roomLines = selectedRooms.map((room) => {
      const ratePerNight = room.ratePerNight || 0;
      const extraAdultRate = room.extraAdultRate || 0;
      const extraChildRate = room.extraChildRate || 0;
      const extraAdultsCount = room.extraAdultsCount || 0;
      const extraChildrenCount = room.extraChildrenCount || 0;
      return {
        ratePerNight: ratePerNight + (extraAdultRate * extraAdultsCount) + (extraChildRate * extraChildrenCount),
        amount: (ratePerNight * currentNights)
          + (extraAdultRate * extraAdultsCount * currentNights)
          + (extraChildRate * extraChildrenCount * currentNights),
      };
    });

    return calculateBookingPriceBreakdown({
      roomLines,
      taxRules,
      gstIncluded: effectiveGstIncluded,
      taxInclusive,
      allowDiscount,
      discountMode,
      discountPercentage,
      discountAmount,
      allowExtraCharges,
      extraChargesAmount,
      extraChargesNote,
      totalBasePrice,
      totalRoomBasePrice,
      totalExtraAdultCharge,
      totalExtraChildCharge,
      gstPercentage,
      hasTaxRules,
    });
  }, [selectedRooms, nights, allowDiscount, discountMode, discountPercentage, discountAmount, effectiveGstIncluded, taxInclusive, allowExtraCharges, extraChargesAmount, extraChargesNote, gstPercentage, hasTaxRules, taxRules]);

  // Add room handler
  const handleAddRoom = (roomData) => {
    setSelectedRooms([...selectedRooms, roomData]);
    setRoomDrawerOpen(false);
  };

  // Remove room handler
  const handleRemoveRoom = (index) => {
    setSelectedRooms(selectedRooms.filter((_, i) => i !== index));
  };

  // Handle room click - open drawer
  const handleRoomClick = (room) => {
    setRoomFormData({
      roomTypeId: room.room_type.id,
      roomId: room.id,
      roomNumber: room.room_number,
      mealPlan: 'EP',
      rooms: 1,
      adults: room.room_type.base_adults || 2,
      children: 0,
      ratePerNight: 0,
      extraAdultCharge: 0,
      extraChildCharge: 0,
    });
    setRoomDrawerOpen(true);
  };

  // Show alert dialog
  const showAlert = (message, title = 'Alert', severity = 'warning') => {
    setAlertMessage(message);
    setAlertTitle(title);
    setAlertSeverity(severity);
    setAlertDialogOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertDialogOpen(false);
  };

  const handleDirtyRoomHousekeeping = useCallback(async (room) => {
    if (!room?.id || housekeepingResolving) return;
    setHousekeepingResolving(true);
    setHousekeepingError('');
    try {
      const cleaningId = await resolveWalkInHousekeepingCleaningId(room.id, pendingCleaningByRoomId);
      setHousekeepingCleaningId(cleaningId);
      await loadPendingCleanings();
    } catch (err) {
      const message = err?.error || err?.detail || err?.message || 'Could not open housekeeping for this room.';
      showAlert(message, 'Room needs cleaning', 'warning');
    } finally {
      setHousekeepingResolving(false);
    }
  }, [housekeepingResolving, pendingCleaningByRoomId, loadPendingCleanings]);

  const handleHousekeepingClose = useCallback(() => {
    setHousekeepingCleaningId(null);
  }, []);

  const handleHousekeepingSuccess = useCallback(async ({ roomId } = {}) => {
    setHousekeepingCleaningId(null);
    if (roomId != null) {
      setPendingCleaningByRoomId((prev) => {
        const next = { ...prev };
        delete next[String(roomId)];
        return next;
      });
      setAllRoomsData((prev) => patchWalkInRoomCleanStatus(prev, roomId, 'clean'));
    }
    await loadPendingCleanings();
  }, [loadPendingCleanings]);

  const handleHousekeepingError = useCallback((message) => {
    setHousekeepingError(message || 'Failed to mark room as clean.');
    loadPendingCleanings();
  }, [loadPendingCleanings]);

  const corporateTaxIdOptions = [
    { value: 'gstin', label: 'GSTIN' },
    { value: 'pan', label: 'PAN' },
    { value: 'tan', label: 'TAN' },
    { value: 'cin', label: 'CIN' },
    { value: 'other', label: 'Other' },
  ];

  const companyCountries = Array.isArray(countriesData?.countries) ? countriesData.countries : [];
  const selectedCompanyCountryObj = companyCountries.find(
    (country) =>
      country?.name === companyDetails.companyCountry ||
      country?.code === companyDetails.companyCountry ||
      country?.name?.toLowerCase() === (companyDetails.companyCountry || '').toLowerCase()
  ) || null;
  const companyStates = selectedCompanyCountryObj?.states || [];
  const selectedCompanyStateObj = companyStates.find(
    (state) => state?.name === companyDetails.companyState
  ) || null;
  const companyCities = selectedCompanyStateObj?.cities || [];

  const hasCompanyDetails = companyDetailsSaved && Boolean(companyDetails.companyName?.trim());

  const validateCompanyDetails = () => {
    const nextErrors = {};
    if (!companyDetails.companyName.trim()) {
      nextErrors.companyName = 'Company name is required';
    }
    if (companyDetails.companyEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(companyDetails.companyEmail.trim())) {
        nextErrors.companyEmail = 'Enter a valid company email';
      }
    }
    setCompanyErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCompanyDetailsSave = () => {
    if (!validateCompanyDetails()) return;
    setCompanyDetailsSaved(true);
    setCompanyDialogOpen(false);
  };

  const handleOpenCompanyDialog = () => {
    setCompanyErrors({});
    setCompanyDetails((prev) => {
      if (companyDetailsSaved) return prev;
      if (!isCompanyFormEffectivelyEmpty(prev)) return prev;
      if (guestProfileCompanySnapshot) {
        return { ...guestProfileCompanySnapshot };
      }
      return prev;
    });
    setCompanyDialogOpen(true);
  };

  /** Clear corporate / company details for this booking only (normal walk-in). Profile snapshot stays for optional re-add. */
  const handleRemoveCompanyDetailsForBooking = () => {
    setCompanyDetails({ ...EMPTY_COMPANY_DETAILS });
    setCompanyDetailsSaved(false);
    setCompanyErrors({});
    setCompanyDialogOpen(false);
  };

  const applyAiGuestFields = useCallback((guest, previews) => {
    const g = guest || {};
    const p =
      typeof previews === 'string'
        ? { frontDataUrl: previews, backDataUrl: null }
        : previews || {};
    const applyTrimmed = (val, setter) => {
      if (val == null || val === '') return;
      const t = String(val).trim();
      if (t !== '') setter(t);
    };
    applyTrimmed(g.guest_title, setGuestTitle);
    applyTrimmed(g.guest_first_name, setGuestFirstName);
    applyTrimmed(g.guest_middle_name, setGuestMiddleName);
    applyTrimmed(g.guest_last_name, setGuestLastName);
    applyTrimmed(g.guest_email, setGuestEmail);
    applyTrimmed(g.guest_address, setGuestAddress);
    applyTrimmed(g.guest_city, setGuestCity);
    applyTrimmed(g.guest_state, setGuestState);
    const countryCombined =
      String(g.guest_country || '').trim() || String(g.nationality || '').trim();
    if (countryCombined) setGuestCountry(countryCombined);
    applyTrimmed(g.guest_pincode, setGuestPincode);
    applyTrimmed(g.guest_dob, setGuestDOB);
    applyTrimmed(g.guest_gender, setGuestGender);
    const docType = g.upload_document_type || 'aadhaar';
    setUploadDocumentType(docType);
    if (g.document_id_number != null && String(g.document_id_number).trim() !== '') {
      setDocumentIdNumber(normalizeDocumentIdInput(docType, g.document_id_number));
    }
    if (p.frontDataUrl) setPassportFrontId(p.frontDataUrl);
    if (p.backDataUrl) setPassportBackId(p.backDataUrl);
  }, []);

  const handleAiCheckinSuccess = useCallback((guest, previews) => {
    setSelectedGuestId(null);
    applyAiGuestFields(guest, previews);
    setAiCheckinOpen(false);
  }, [applyAiGuestFields]);

  // Step validation
  const validateStep1 = () => {
    if (!checkInDate || !checkOutDate) {
      showAlert('Please select check-in and check-out dates', 'Validation Error', 'warning');
      return false;
    }
    if (checkOutDate <= checkInDate) {
      showAlert('Check-out date must be after check-in date', 'Validation Error', 'warning');
      return false;
    }
    if (selectedRooms.length === 0) {
      showAlert('Please select at least one room', 'Validation Error', 'warning');
      return false;
    }
    return true;
  };

  // Navigation handlers
  const handleNext = () => {
    if (activeStep === STEPS.ROOMS) {
      if (!validateStep1()) return;
      setActiveStep(STEPS.GUEST_DETAILS);
    } else if (activeStep === STEPS.GUEST_DETAILS) {
      // Validate all fields and show errors if invalid
      if (validateGuestDetailsRef.current && typeof validateGuestDetailsRef.current === 'function') {
        const isValid = validateGuestDetailsRef.current();
        if (isValid) {
          submitIdempotencyKeyRef.current = null;
          setActiveStep(STEPS.REVIEW);
        }
        // If invalid, validation errors are shown inline - don't proceed
      } else {
        // If validation function not available, proceed if form appears valid
        if (isGuestDetailsValid) {
          submitIdempotencyKeyRef.current = null;
          setActiveStep(STEPS.REVIEW);
        }
      }
    }
  };

  const handleBack = () => {
    if (activeStep === STEPS.REVIEW) {
      submitIdempotencyKeyRef.current = null;
    }
    setActiveStep(activeStep - 1);
  };

  useEffect(() => {
    if (!isMobileLayout || activeStep !== STEPS.GUEST_DETAILS) return undefined;

    const scrollFrame = requestAnimationFrame(() => {
      const target = guestDetailsTopRef.current || walkInShellRef.current;
      scrollMobileStepTopIntoView(target);
    });

    return () => cancelAnimationFrame(scrollFrame);
  }, [activeStep, isMobileLayout]);

  // Submit handler - Single API call for all bookings
  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    // Validate payment method for full/partial payment
    if ((paymentType === 'full' || paymentType === 'partial') && !paymentMethod) {
      showAlert('Please select a payment method for full or partial payment', 'Validation Error', 'warning');
      return;
    }

    // Open dialog immediately with processing state
    setIsSubmitting(true);
    setSuccessDialogOpen(true);
    setBookingData({ processing: true });

    try {
      // Prepare all rooms data - walk-in uses single check-in and check-out dates for all rooms
      // Recalculate prices based on current nights (selectedDays) instead of stored values
      const currentNights = nights || 1;
      const allRoomsData = selectedRooms.map((room) => {
        // Recalculate prices based on current nights
        const ratePerNight = room.ratePerNight || 0;
        const extraAdultRate = room.extraAdultRate || 0;
        const extraChildRate = room.extraChildRate || 0;
        const extraAdultsCount = room.extraAdultsCount || 0;
        const extraChildrenCount = room.extraChildrenCount || 0;
        
        // Recalculate based on current nights
        const basePrice = ratePerNight * currentNights;
        const extraAdultCharge = extraAdultRate * extraAdultsCount * currentNights;
        const extraChildCharge = extraChildRate * extraChildrenCount * currentNights;
        
        return {
          roomTypeId: room.roomTypeId,
          roomId: room.roomId,
          roomNumber: room.roomNumber, // Include room number for invoice display
          mealPlan: room.mealPlan || 'EP',
          rooms: 1, // Walk-in always has 1 room
          adults: room.adults,
          children: room.children,
          basePrice: basePrice,
          extraAdultCharge: extraAdultCharge,
          extraChildCharge: extraChildCharge,
          discount: room.discount || 0,
          // Walk-in uses single check-in and check-out dates for all rooms
          // Room-specific dates are ignored
        };
      });

      // Prepare guest data
      // Walk-in "No Document" should be submitted as empty document type.
      const normalizedUploadDocumentType = uploadDocumentType === 'no_document' ? '' : (uploadDocumentType || '');
      const guestData = {
      ...(selectedGuestId ? { id: selectedGuestId } : {}),
        title: guestTitle || '',
        first_name: guestFirstName.trim(),
        middle_name: guestMiddleName.trim() || '',
        last_name: guestLastName.trim(),
        email: guestEmail.trim() || '',
        phone: guestPhone.trim().replace(/\D/g, ''), // Only digits
        country_code: guestPhoneCountryCode || '+91',
        alternate_phone: guestAlternatePhone.trim().replace(/\D/g, '') || '', // Only digits
        alternate_phone_country_code: guestAlternatePhoneCountryCode || '+91',
        address_line1: guestAddress.trim() || '',
      city: guestCity.trim() || '',
      state: guestState.trim() || '',
      country: (() => {
        if (!guestCountry) return '';
        const countryObj = countriesData.countries.find((c) => c.name === guestCountry || c.code === guestCountry);
        return countryObj ? countryObj.code : guestCountry.trim();
      })(),
      pincode: guestPincode.trim() || '',
      dob: guestDOB || '',
      gender: guestGender || '',
      purpose_of_stay: purposeOfStay || '',
      purpose_of_stay_note:
        purposeOfStay === 'other' ? (purposeOfStayNote || '').trim() : '',
      eta: guestETA || '',
      guest_note: guestNote || '',
      booking_reference: bookingReference || '',
      booking_source: hasCompanyDetails ? 'corporate' : 'walk_in',
      remarks: remarks || '',
      company_name: hasCompanyDetails ? (companyDetails.companyName || '') : '',
      company_tax_id_type: hasCompanyDetails ? (companyDetails.taxIdType || '') : '',
      company_tax_id_number: hasCompanyDetails ? (companyDetails.taxIdNumber || '') : '',
      company_phone: hasCompanyDetails ? (companyDetails.companyPhone || '') : '',
      company_email: hasCompanyDetails ? (companyDetails.companyEmail || '') : '',
      company_address: hasCompanyDetails ? (companyDetails.companyAddress || '') : '',
      company_city: hasCompanyDetails ? (companyDetails.companyCity || '') : '',
      company_state: hasCompanyDetails ? (companyDetails.companyState || '') : '',
      company_country: hasCompanyDetails ? (companyDetails.companyCountry || '') : '',
      company_pincode: hasCompanyDetails ? (companyDetails.companyPincode || '') : '',
      company_postal_code: hasCompanyDetails ? (companyDetails.companyPincode || '') : '',
      document_type: normalizedUploadDocumentType,
      id_number: normalizeDocumentIdForSubmit(normalizedUploadDocumentType, documentIdNumber) || '',
      // Send document when base64 (new upload) or URL (existing guest's stored document)
      id_front: (passportFrontId && typeof passportFrontId === 'string' && (passportFrontId.startsWith('data:image') || passportFrontId.startsWith('http://') || passportFrontId.startsWith('https://'))) ? passportFrontId : '',
      id_back: (passportBackId && typeof passportBackId === 'string' && (passportBackId.startsWith('data:image') || passportBackId.startsWith('http://') || passportBackId.startsWith('https://'))) ? passportBackId : '',
      ...(isForeignGuestCountry(guestCountry, hotelCountry)
        ? foreignDetailsToGuestApiPayload(foreignGuestDetails)
        : {}),
    };

      // Prepare single form data with all rooms
      // Walk-in uses single check-in and check-out dates for all rooms
      if (!submitIdempotencyKeyRef.current) {
        submitIdempotencyKeyRef.current = generateIdempotencyKey();
      }
      const formData = {
        bookingType: bookingType,
        checkInDate: checkInDate.format('YYYY-MM-DD'),
        checkOutDate: checkOutDate.format('YYYY-MM-DD'),
        rooms: allRoomsData,
        guest: guestData,
        gstIncluded: effectiveGstIncluded,
        taxInclusive: taxInclusive,
        allowDiscount: allowDiscount,
        discountPercentage: allowDiscount && discountMode === 'percentage' ? discountPercentage : 0,
        discountAmount: allowDiscount && discountMode === 'amount' ? discountAmount : 0,
        allowExtraCharges,
        extraChargesAmount: allowExtraCharges ? extraChargesAmount : 0,
        extraChargesNote: allowExtraCharges ? extraChargesNote.trim() : '',
        paymentType: paymentType,
        paymentAmount:
          paymentType === 'full'
            ? toCurrencyAmount(priceBreakdown?.total)
            : paymentAmount
              ? toCurrencyAmount(paymentAmount)
              : 0,
        paymentMethod: paymentMethod || '',
        referenceNumber: referenceNumber || '',
        paymentDocument: paymentDocument || '',
        collectionDate: toPaymentProcessedAtIso(collectionDate) || '',
        idempotencyKey: submitIdempotencyKeyRef.current,
      };

      // Single API call - backend handles grouping by dates and creating multiple bookings
      const response = await submitWalkIn(formData);
      
      // Backend returns bookings array with all created bookings
      const allBookings = response.bookings || [];
      
      const combinedResponse = {
        success: response.success || true,
        message: response.message || `Successfully created ${allBookings.length} booking(s)`,
        bookings: allBookings,
        total_bookings: response.total_bookings || allBookings.length,
      };
      
      setBookingData(combinedResponse);
      const bidList = [];
      (allBookings || []).forEach((booking) => {
        const idCandidate = booking?.booking_id ?? booking?.id ?? null;
        const parsedId = Number(idCandidate);
        if (Number.isInteger(parsedId) && parsedId > 0) bidList.push(parsedId);
      });
      const uniqIds = [...new Set(bidList)];
      const firstPlId = uniqIds.length
        ? uniqIds[0]
        : Number(response.booking_id ?? response.id ?? 0);
      const plId = Number.isInteger(firstPlId) && firstPlId > 0 ? firstPlId : 0;
      const dueForLink = computeAmountDueForPaymentLink({
        total: priceBreakdown?.total,
        paymentMethod,
        paymentType,
        paymentAmount,
        isWalkIn: true,
      });
      setSuccessPaymentLinkBookingId(plId);
      setSuccessPaymentLinkAmount(dueForLink);
      setShowPaymentLinkOnSuccess(plId > 0 && dueForLink > 0 && canSendPaymentLink);
      setIsSubmitting(false);
    } catch (error) {setIsSubmitting(false);
      setSuccessDialogOpen(false);
      let errorMsg = 'Error submitting booking. Please try again.';
      if (error.response && error.response.data) {
        if (error.response.data.error) {
          errorMsg = error.response.data.error;
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
    }
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setErrorMessage('');
  };

  const handleCloseSuccessDialog = () => {
    submitIdempotencyKeyRef.current = null;
    setSuccessDialogOpen(false);
    setSuccessActionLoading(null);
    setShowPaymentLinkOnSuccess(false);
    setSuccessPaymentLinkBookingId(0);
    setSuccessPaymentLinkAmount(0);
    navigate('/booking/');
  };

  const getActionBookingIds = () => {
    if (!bookingData) return [];
    const bookingIds = [];

    if (Array.isArray(bookingData.bookings) && bookingData.bookings.length > 0) {
      bookingData.bookings.forEach((booking) => {
        const idCandidate = booking?.booking_id ?? booking?.id ?? null;
        const parsedId = Number(idCandidate);
        if (Number.isInteger(parsedId) && parsedId > 0) {
          bookingIds.push(parsedId);
        }
      });
    }

    if (bookingIds.length > 0) {
      return [...new Set(bookingIds)];
    }

    const fallbackId = Number(bookingData.booking_id ?? bookingData.id ?? null);
    if (Number.isInteger(fallbackId) && fallbackId > 0) {
      return [fallbackId];
    }
    return [];
  };

  const extractActionErrorMessage = (error, fallbackMessage) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage;

  const getGroupBookingInfo = () => {
    if (!bookingData) return null;
    const code =
      bookingData.group_booking?.group_booking_code
      || bookingData.group_booking?.code
      || bookingData.group_booking_code
      || null;
    if (!code) return null;
    return {
      ...(bookingData.group_booking || {}),
      group_booking_code: code,
      total_amount: bookingData.group_booking?.total_amount ?? null,
    };
  };

  const getInvoiceActionBookingIds = () => {
    const ids = getActionBookingIds();
    if (getGroupBookingInfo() && ids.length > 1) {
      return [ids[0]];
    }
    return ids;
  };

  const handleDownloadInvoice = async () => {
    const bookingIds = getInvoiceActionBookingIds();
    if (!bookingIds.length) {
      showAlert('Booking details not found for invoice download', 'Warning', 'warning');
      return;
    }

    const group = getGroupBookingInfo();
    setSuccessActionLoading('invoice');
    try {
      for (const bookingId of bookingIds) {
        await downloadBookingInvoice(bookingId, 'hotel', { groupInvoice: Boolean(group) });
      }
      showAlert(
        group
          ? `Group invoice downloaded (${group.group_booking_code})`
          : bookingIds.length > 1
            ? `Invoices downloaded for ${bookingIds.length} bookings`
            : 'Invoice downloaded successfully',
        'Success',
        'success'
      );
    } catch (error) {
      showAlert(extractActionErrorMessage(error, 'Invoice download failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  };

  const handleDownloadVoucher = async () => {
    const bookingIds = getInvoiceActionBookingIds();
    if (!bookingIds.length) {
      showAlert('Booking details not found for voucher download', 'Warning', 'warning');
      return;
    }

    const group = getGroupBookingInfo();
    setSuccessActionLoading('voucher');
    try {
      for (const bookingId of bookingIds) {
        await downloadBookingConfirmation(bookingId, { groupVoucher: Boolean(group) });
      }
      showAlert(
        group
          ? `Group voucher downloaded (${group.group_booking_code})`
          : bookingIds.length > 1
            ? `Vouchers downloaded for ${bookingIds.length} bookings`
            : 'Voucher downloaded successfully',
        'Success',
        'success'
      );
    } catch (error) {
      showAlert(extractActionErrorMessage(error, 'Voucher download failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  };

  const handleSendWhatsApp = async () => {
    const bookingIds = getInvoiceActionBookingIds();
    const phoneNumber = guestPhone?.replace(/\D/g, '');
    const group = getGroupBookingInfo();
    const bookingCode =
      group?.group_booking_code
      || bookingData?.bookings?.[0]?.booking_code
      || bookingData?.booking_code
      || bookingData?.code;

    if (!bookingIds.length) {
      showAlert('Booking details not found for sending WhatsApp', 'Warning', 'warning');
      return;
    }
    if (!phoneNumber) {
      showAlert('Phone number is required to send WhatsApp message', 'Warning', 'warning');
      return;
    }

    setSuccessActionLoading('whatsapp');
    try {
      let sentCount = 0;
      const errors = [];
      let lastRes = null;

      for (const bookingId of bookingIds) {
        const res = await sendBookingConfirmation(bookingId, {
          send_email: false,
          send_whatsapp: true,
          groupInvoice: Boolean(group),
          groupVoucher: Boolean(group),
        });
        lastRes = res;
        if (res?.whatsapp_sent) {
          sentCount += 1;
        } else if (Array.isArray(res?.errors) && res.errors.length > 0) {
          errors.push(...res.errors);
        }
      }

      if (sentCount > 0) {
        const sentDocs =
          Array.isArray(lastRes?.whatsapp_sent_documents) && lastRes.whatsapp_sent_documents.length
            ? lastRes.whatsapp_sent_documents.join(' and ')
            : group
              ? 'group voucher and invoice'
              : 'voucher and invoice';
        showAlert(
          formatWhatsAppSuccessMessage(lastRes, {
            prefix: group
              ? `${sentDocs.charAt(0).toUpperCase()}${sentDocs.slice(1)} sent on WhatsApp (${group.group_booking_code})`
              : `${sentDocs.charAt(0).toUpperCase()}${sentDocs.slice(1)} sent on WhatsApp`,
            bookingCode,
          }),
          'Success',
          'success'
        );
      } else {
        showAlert(errors[0] || 'WhatsApp not sent', 'Warning', 'warning');
      }
    } catch (error) {
      showAlert(extractActionErrorMessage(error, 'WhatsApp send failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  };

  const handleSendEmail = async () => {
    const bookingIds = getInvoiceActionBookingIds();
    const email = guestEmail;
    const group = getGroupBookingInfo();

    if (!bookingIds.length) {
      showAlert('Booking details not found for sending email', 'Warning', 'warning');
      return;
    }
    if (!email) {
      showAlert('Email address is required to send email', 'Warning', 'warning');
      return;
    }

    setSuccessActionLoading('email');
    try {
      let sentCount = 0;
      const errors = [];

      for (const bookingId of bookingIds) {
        const res = await sendBookingConfirmation(bookingId, {
          send_email: true,
          send_whatsapp: false,
          groupInvoice: Boolean(group),
          groupVoucher: Boolean(group),
        });
        if (res?.email_sent) {
          sentCount += 1;
        } else if (Array.isArray(res?.errors) && res.errors.length > 0) {
          errors.push(...res.errors);
        }
      }

      if (sentCount > 0) {
        const successMessage = group
          ? `Group voucher and invoice sent (${group.group_booking_code})`
          : bookingIds.length > 1
            ? `Confirmation and invoice sent by email for ${sentCount}/${bookingIds.length} bookings`
            : 'Confirmation and invoice sent to guest email';
        showAlert(successMessage, 'Success', 'success');
      } else {
        showAlert(errors[0] || 'Email not sent', 'Warning', 'warning');
      }
    } catch (error) {
      showAlert(extractActionErrorMessage(error, 'Email send failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  };

  const stepLabels = [
    { label: 'Rooms', shortLabel: 'Rooms', description: 'Select rooms' },
    { label: 'Guest Details', shortLabel: 'Guest', description: 'Guest information' },
    { label: 'Review', shortLabel: 'Review', description: 'Review and confirm' },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box ref={walkInShellRef} sx={getWalkInShellSx(currentTheme)}>
        <Box sx={{ mb: isMobileLayout ? 1 : 3 }}>
          <DashboardHeader
            title="Walk-In"
            forceDark={isMobileLayout}
            navLinks={isMobileLayout ? [] : [
              { url: '/booking/dashboard/', label: 'Booking Dashboard' },
              { url: '/booking/list/', label: 'Booking List' },
              { url: '/booking/reservation/', label: 'Reservation' },
            ]}
          />
        </Box>

        {useWalkInFlowLayout && (
          <WalkInMobileStepBar steps={stepLabels} activeStep={activeStep} />
        )}

        <Paper elevation={0} sx={getWalkInOuterPaperSx(isDark, isMobileLayout || useWalkInFlowLayout)}>
          <Grid container spacing={useWalkInFlowLayout ? 0 : 3}>
        {/* Left Sidebar - Steps (desktop only) */}
        {!useWalkInFlowLayout && (
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              position: 'sticky',
              top: 20,
              backgroundColor: isDark ? '#232329' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              p: 3,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                mb: 3,
                fontWeight: 600,
              }}
            >
              Walk-In
            </Typography>
            <Stepper
              activeStep={activeStep}
              orientation="vertical"
              sx={{
                '& .MuiStepLabel-label': {
                  color: isDark ? '#999' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  '&.Mui-active': {
                    color: '#4ce1b6',
                    fontWeight: 600,
                  },
                  '&.Mui-completed': {
                    color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  },
                },
                '& .MuiStepIcon-root': {
                  color: isDark ? '#333246' : '#e0e0e0',
                  '&.Mui-active': {
                    color: '#4ce1b6',
                  },
                  '&.Mui-completed': {
                    color: '#4ce1b6',
                  },
                },
                '& .MuiStepConnector-root': {
                  '& .MuiStepConnector-line': {
                    borderColor: isDark ? '#333246' : '#e0e0e0',
                  },
                },
              }}
            >
              {stepLabels.map((step, index) => (
                <Step key={step.label} active={index === activeStep} completed={index < activeStep}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        </Grid>
        )}

        {/* Right Side - Form Content */}
        <Grid
          item
          xs={12}
          md={useWalkInFlowLayout ? 12 : 9}
          sx={activeStep === STEPS.ROOMS && !useWalkInFlowLayout ? getWalkInRoomsStepGridItemSx() : undefined}
        >
          <Paper
            elevation={0}
            sx={{
              ...getWalkInFormPaperSx(isDark, isMobileLayout || useWalkInFlowLayout),
              ...(activeStep === STEPS.ROOMS ? getWalkInRoomsStepPaperSx(useWalkInFlowLayout) : {}),
            }}
          >
            <Box sx={activeStep === STEPS.ROOMS && !useWalkInFlowLayout ? getWalkInRoomsStepBodySx() : undefined}>
              {activeStep === STEPS.ROOMS && (
                <RoomsStep
                  checkInDate={checkInDate}
                  checkOutDate={checkOutDate}
                  onCheckInDateChange={setCheckInDate}
                  onCheckOutDateChange={setCheckOutDate}
                  nights={nights}
                  selectedRooms={selectedRooms}
                  allRoomsData={allRoomsData}
                  loadingRooms={loadingRooms}
                  onAddRoom={handleAddRoom}
                  onRemoveRoom={handleRemoveRoom}
                  roomDrawerOpen={roomDrawerOpen}
                  onRoomDrawerOpen={() => setRoomDrawerOpen(true)}
                  onRoomDrawerClose={() => setRoomDrawerOpen(false)}
                  roomFormData={roomFormData}
                  onRoomFormDataChange={setRoomFormData}
                  bookingType={bookingType}
                  isWalkIn={true}
                  selectedDays={selectedDays}
                  onDaysChange={handleDaysChange}
                  groupView={roomGroupView}
                  onGroupViewChange={(nextView) => {
                    setRoomGroupView(nextView);
                    writeStoredWalkInGroupView(nextView);
                  }}
                  themeMode={currentTheme}
                  onDirtyRoomHousekeeping={handleDirtyRoomHousekeeping}
                />
              )}

              {activeStep === STEPS.GUEST_DETAILS && (
                <Box ref={guestDetailsTopRef} sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: aiCheckinOpen ? 'blur(10px)' : 'none',
                      opacity: aiCheckinOpen ? 0.55 : 1,
                      transition: 'filter 0.35s ease, opacity 0.35s ease',
                      pointerEvents: aiCheckinOpen ? 'none' : 'auto',
                      userSelect: aiCheckinOpen ? 'none' : 'auto',
                    }}
                  >
                    <GuestDetailsStep
                  guestTitle={guestTitle}
                  guestFirstName={guestFirstName}
                  guestMiddleName={guestMiddleName}
                  guestLastName={guestLastName}
                  onGuestTitleChange={setGuestTitle}
                  onGuestFirstNameChange={setGuestFirstName}
                  onGuestMiddleNameChange={setGuestMiddleName}
                  onGuestLastNameChange={setGuestLastName}
                  guestEmail={guestEmail}
                  guestPhone={guestPhone}
                  guestPhoneCountryCode={guestPhoneCountryCode}
                  guestAlternatePhone={guestAlternatePhone}
                  guestAlternatePhoneCountryCode={guestAlternatePhoneCountryCode}
                  onGuestEmailChange={setGuestEmail}
                  onGuestPhoneChange={setGuestPhone}
                  onGuestPhoneCountryCodeChange={handleGuestPhoneCountryCodeChange}
                  onSelectedGuestIdChange={setSelectedGuestId}
                  selectedGuestId={selectedGuestId}
                  onGuestAlternatePhoneChange={setGuestAlternatePhone}
                  onGuestAlternatePhoneCountryCodeChange={handleGuestAlternatePhoneCountryCodeChange}
                  guestAddress={guestAddress}
                  guestCity={guestCity}
                  guestState={guestState}
                  guestCountry={guestCountry}
                  guestPincode={guestPincode}
                  onGuestAddressChange={setGuestAddress}
                  onGuestCityChange={setGuestCity}
                  onGuestStateChange={setGuestState}
                  onGuestCountryChange={setGuestCountry}
                  onGuestPincodeChange={setGuestPincode}
                  guestDOB={guestDOB}
                  guestGender={guestGender}
                  onGuestDOBChange={setGuestDOB}
                  onGuestGenderChange={setGuestGender}
                  guestETA={guestETA}
                  guestNote={guestNote}
                  bookingReference={bookingReference}
                  remarks={remarks}
                  onGuestETAChange={setGuestETA}
                  onGuestNoteChange={setGuestNote}
                  onBookingReferenceChange={setBookingReference}
                  uploadDocumentType={uploadDocumentType}
                  onUploadDocumentChange={setUploadDocumentType}
                  documentIdNumber={documentIdNumber}
                  onDocumentIdNumberChange={setDocumentIdNumber}
                  onRemarksChange={setRemarks}
                  purposeOfStay={purposeOfStay}
                  purposeOfStayNote={purposeOfStayNote}
                  onPurposeOfStayChange={setPurposeOfStay}
                  onPurposeOfStayNoteChange={setPurposeOfStayNote}
                  passportFrontId={passportFrontId}
                  passportBackId={passportBackId}
                  onPassportFrontIdChange={setPassportFrontId}
                  onPassportBackIdChange={setPassportBackId}
                  hotelCountry={hotelCountry}
                  foreignGuestRoomNumber={selectedRooms[0]?.roomNumber || ''}
                  foreignGuestCheckInDate={checkInDate}
                  foreignGuestDetails={foreignGuestDetails}
                  onForeignGuestDetailsChange={setForeignGuestDetails}
                  isWalkIn={true}
                  hideGuestInformationHeading={isMobileLayout}
                  onValidationChange={setIsGuestDetailsValid}
                  onValidateRequest={(fn) => { validateGuestDetailsRef.current = fn; }}
                  headingActions={
                    <Box sx={getWalkInHeadingActionsSx(isMobileLayout)}>
                      {canManageAiCheckin && (
                        <AiGradientButton onClick={() => setAiCheckinOpen(true)}>
                          AI Check-in
                        </AiGradientButton>
                      )}
                      <CompanyDetailsHeadingActions
                        hasCompanyDetails={hasCompanyDetails}
                        companyName={companyDetails.companyName}
                        isDark={isDark}
                        onEdit={handleOpenCompanyDialog}
                        onRemove={handleRemoveCompanyDetailsForBooking}
                      />
                    </Box>
                  }
                    />
                  </Box>
                  {canManageAiCheckin && (
                    <WalkInAiCheckinOverlay
                      open={aiCheckinOpen}
                      onClose={() => setAiCheckinOpen(false)}
                      onSuccess={handleAiCheckinSuccess}
                      isDark={isDark}
                    />
                  )}
                </Box>
              )}

              {activeStep === STEPS.REVIEW && (
                <ReviewStep
                  checkInDate={checkInDate}
                  checkOutDate={checkOutDate}
                  nights={nights}
                  rooms={selectedRooms}
                  guestName={`${guestFirstName} ${guestLastName}`.trim()}
                  guestEmail={guestEmail}
                  guestPhone={guestPhone}
                  guestAddress={guestAddress}
                  gstIncluded={effectiveGstIncluded}
                  taxInclusive={taxInclusive}
                  allowDiscount={allowDiscount}
                  discountMode={discountMode}
                  discountPercentage={discountPercentage}
                  discountAmount={discountAmount}
                  maxDiscountAmount={priceBreakdown?.priceBeforeDiscount || 0}
                  bookingType={bookingType}
                  autoGstEnabled={autoGstEnabled}
                  onGstIncludedChange={setGstIncluded}
                  onTaxInclusiveChange={setTaxInclusive}
                  onAllowDiscountChange={setAllowDiscount}
                  onDiscountModeChange={setDiscountMode}
                  onDiscountPercentageChange={setDiscountPercentage}
                  onDiscountAmountChange={setDiscountAmount}
                  allowExtraCharges={allowExtraCharges}
                  extraChargesAmount={extraChargesAmount}
                  extraChargesNote={extraChargesNote}
                  extraChargesItems={extraChargesItems}
                  extraChargesBaseAmount={extraChargesBaseAmount}
                  extraChargesGstAmount={extraChargesGstAmount}
                  onAllowExtraChargesChange={setAllowExtraCharges}
                  onExtraChargesAmountChange={setExtraChargesAmount}
                  onExtraChargesNoteChange={setExtraChargesNote}
                  onExtraChargesItemsChange={setExtraChargesItems}
                  onExtraChargesBaseAmountChange={setExtraChargesBaseAmount}
                  onExtraChargesGstAmountChange={setExtraChargesGstAmount}
                  priceBreakdown={priceBreakdown}
                  isWalkIn={true}
                  hasTaxRules={hasTaxRules}
                  paymentType={paymentType}
                  paymentAmount={paymentAmount}
                  onPaymentTypeChange={setPaymentType}
                  onPaymentAmountChange={(value) => {
                    setPaymentAmount(toCurrencyAmount(value));
                  }}
                  paymentMethod={paymentMethod}
                  referenceNumber={referenceNumber}
                  paymentDocument={paymentDocument}
                  onPaymentMethodChange={setPaymentMethod}
                  onReferenceNumberChange={setReferenceNumber}
                  onPaymentDocumentChange={setPaymentDocument}
                  collectionDate={collectionDate}
                  onCollectionDateChange={setCollectionDate}
                  suppressPaymentLinkOverlay
                />
              )}
            </Box>

            <Box sx={getWalkInFooterSx(isDark, isMobileLayout || useWalkInFlowLayout)}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                size={isMobileLayout || useWalkInFlowLayout ? 'medium' : 'large'}
                sx={getWalkInFooterButtonSx('back', isDark, isMobileLayout || useWalkInFlowLayout)}
              >
                Back
              </Button>
              {activeStep === stepLabels.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  size={isMobileLayout || useWalkInFlowLayout ? 'medium' : 'large'}
                  sx={getWalkInFooterButtonSx('next', isDark, isMobileLayout || useWalkInFlowLayout)}
                >
                  {isSubmitting ? 'Processing...' : 'Confirm Booking'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  size={isMobileLayout || useWalkInFlowLayout ? 'medium' : 'large'}
                  sx={getWalkInFooterButtonSx('next', isDark, isMobileLayout || useWalkInFlowLayout)}
                >
                  Next
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
        </Paper>

      {/* Success Dialog - Creative 3D Design */}
      <Dialog
        open={successDialogOpen}
        onClose={bookingData?.processing ? undefined : handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobileLayout}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
            background: isDark
              ? drawerTheme.surfaceGradient
              : `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
            border: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            boxShadow: isDark 
              ? '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(76, 225, 182, 0.1), inset 0 1px 0 rgba(255,255,255,0.1)'
              : '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(76, 225, 182, 0.1), inset 0 1px 0 rgba(255,255,255,0.8)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, #4ce1b6 0%, #3ab896 50%, #4ce1b6 100%)`,
              boxShadow: '0 2px 8px rgba(76, 225, 182, 0.4)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1.5,
            pt: 2.5,
            px: 2.5,
            borderBottom: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            background: isDark 
              ? `linear-gradient(135deg, rgba(76, 225, 182, 0.05) 0%, rgba(58, 184, 150, 0.05) 100%)`
              : `linear-gradient(135deg, rgba(76, 225, 182, 0.03) 0%, rgba(58, 184, 150, 0.03) 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {bookingData?.processing ? (
              <CircularProgress size={24} sx={{ color: '#4ce1b6' }} />
            ) : (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, #4ce1b6 0%, #3ab896 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(76, 225, 182, 0.4)',
                }}
              >
                <CheckCircleIcon sx={{ color: '#000', fontSize: 20 }} />
              </Box>
            )}
            <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600, fontSize: '1rem' }}>
              {bookingData?.processing
                ? 'Processing Booking...'
                : bookingData?.bookings && bookingData.bookings.length > 1
                ? `Bookings Confirmed! (${bookingData.bookings.length})`
                : 'Booking Confirmed!'}
            </Typography>
          </Box>
          {!bookingData?.processing && (
            <IconButton 
              onClick={handleCloseSuccessDialog} 
              size="small" 
              sx={{ 
                color: isDark ? drawerTheme.subtleText : '#666',
                '&:hover': {
                  backgroundColor: isDark ? drawerTheme.chipBg : '#f5f5f5',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
              {bookingData?.processing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                  <CircularProgress size={48} sx={{ color: '#4ce1b6', mb: 2 }} />
                  <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', textAlign: 'center', fontSize: '0.875rem' }}>
                    Please wait while we process your booking...
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', mt: 0.5, textAlign: 'center', fontSize: '0.75rem' }}>
                    This may take a few moments
                  </Typography>
                </Box>
              ) : bookingData && (
            <Box>
              {bookingData.bookings && bookingData.bookings.length > 1 ? (
                // Multiple bookings
                <Box>
                  <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', mb: 1.5, fontSize: '0.75rem', textAlign: 'center' }}>
                    {bookingData.message || `Successfully created ${bookingData.bookings.length} bookings`}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {bookingData.bookings.map((booking, index) => (
                      <Grid item xs={12} key={booking.booking_id || index}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            backgroundColor: isDark ? '#333246' : '#f8f9fa',
                            borderRadius: 1.5,
                            border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                            boxShadow: `0 2px 8px rgba(76, 225, 182, 0.15), inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)'}`,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                            Booking {index + 1}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.875rem' }}>
                            {booking.booking_code || booking.booking_id || 'N/A'}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      backgroundColor: isDark ? '#333246' : '#f8f9fa',
                      borderRadius: 1.5,
                      border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                      boxShadow: `0 2px 8px rgba(76, 225, 182, 0.1)`,
                    }}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                          Guest
                        </Typography>
                        <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                          {guestFirstName} {guestLastName || ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                          Phone
                        </Typography>
                        <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                          {guestPhone || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                          Rooms
                        </Typography>
                        <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                          {selectedRooms.length}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                          Amount
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.8rem' }}>
                          {selectedCurrencySymbol}{toCurrencyAmount(priceBreakdown.total).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              ) : bookingData.bookings && bookingData.bookings.length === 1 ? (
                // Single booking in bookings array (walk-in standard format)
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    backgroundColor: isDark ? '#333246' : '#f8f9fa',
                    borderRadius: 1.5,
                    border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                    boxShadow: `0 2px 8px rgba(76, 225, 182, 0.15), inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)'}`,
                  }}
                >
                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                    Booking Code
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.875rem', mb: 1.5 }}>
                    {bookingData.bookings[0].booking_code || bookingData.bookings[0].booking_id || 'N/A'}
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                        Guest
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                        {guestFirstName} {guestLastName || ''}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                        {guestPhone || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                        Rooms
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                        {selectedRooms.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                        Amount
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.8rem' }}>
                        {selectedCurrencySymbol}{priceBreakdown.total.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ) : (
                // Fallback for backward compatibility (no bookings array or empty)
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    backgroundColor: isDark ? '#333246' : '#f8f9fa',
                    borderRadius: 1.5,
                    border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                    boxShadow: `0 2px 8px rgba(76, 225, 182, 0.15), inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)'}`,
                  }}
                >
                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                    Booking Code
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.875rem', mb: 1.5 }}>
                    {bookingData.booking_code || bookingData.code || bookingData.booking_id || 'N/A'}
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                        Guest
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                        {guestFirstName} {guestLastName || ''}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                        {guestPhone || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                        Rooms
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                        {selectedRooms.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                        Amount
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.8rem' }}>
                        {selectedCurrencySymbol}{priceBreakdown.total.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        {!bookingData?.processing && (
          <DialogActions
            sx={{
              p: 2,
              pt: 1.25,
              flexDirection: 'column',
              gap: 0,
              alignItems: 'stretch',
              borderTop: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            }}
          >
            <BookingConfirmedActionsContent
              isDark={isDark}
              accent="#4ce1b6"
              successActionLoading={successActionLoading}
              onDownloadInvoice={handleDownloadInvoice}
              onDownloadVoucher={handleDownloadVoucher}
              onSendWhatsApp={handleSendWhatsApp}
              onSendEmail={handleSendEmail}
              onDone={handleCloseSuccessDialog}
              invoiceButtonLabel={
                getGroupBookingInfo() ? 'Download group invoice' : 'Download invoice'
              }
              voucherButtonLabel={
                getGroupBookingInfo() ? 'Download group voucher' : 'Download voucher'
              }
              showPaymentLink={showPaymentLinkOnSuccess}
              paymentLinkBookingId={successPaymentLinkBookingId}
              paymentLinkBooking={successPaymentLinkBooking}
              paymentLinkAmount={successPaymentLinkAmount}
              paymentLinkCurrency={successPaymentLinkBooking?.currency || selectedCurrency}
              canSendPaymentLinkEmail={canSendPaymentLinkEmail}
              canSendPaymentLinkWhatsApp={canSendPaymentLinkWhatsApp}
              onPaymentLinkSent={(msg) => showAlert(msg, 'Success', 'success')}
            />
          </DialogActions>
        )}
      </Dialog>

      {/* Error Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: isDark ? '#2a2a31' : '#ffffff',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#f44336', fontSize: 32 }} />
            <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
              Error
            </Typography>
          </Box>
          <IconButton onClick={handleCloseErrorDialog} size="small" sx={{ color: isDark ? '#999' : '#666' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: isDark ? '#ddd' : '#333', mt: 1 }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleCloseErrorDialog}
            variant="contained"
            sx={{
              backgroundColor: '#f44336',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialogOpen}
        onClose={handleCloseAlert}
        title={alertTitle}
        message={alertMessage}
        severity={alertSeverity}
      />

      {/* Company Details Drawer */}
      <Drawer
        anchor="right"
        open={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, { width: BOOKING_DRAWER_WIDTH_SX }),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                Company details
              </Typography>
            </Box>
            <IconButton
              onClick={() => setCompanyDialogOpen(false)}
              sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, ...companyDrawerOverlays.fieldShellSx }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Tax ID Type"
                    value={companyDetails.taxIdType}
                    onChange={(e) => setCompanyDetails((prev) => ({ ...prev, taxIdType: e.target.value }))}
                    SelectProps={{ MenuProps: companyDrawerOverlays.selectMenuProps }}
                  >
                    {corporateTaxIdOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Enter Tax Identification Number"
                    value={companyDetails.taxIdNumber}
                    onChange={(e) => setCompanyDetails((prev) => ({ ...prev, taxIdNumber: e.target.value }))}
                    error={Boolean(companyErrors.taxIdNumber)}
                    helperText={companyErrors.taxIdNumber || ''}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={companyDetails.companyName}
                onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyName: e.target.value }))}
                error={Boolean(companyErrors.companyName)}
                helperText={companyErrors.companyName || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Phone"
                value={companyDetails.companyPhone}
                onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyPhone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Email"
                value={companyDetails.companyEmail}
                onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyEmail: e.target.value }))}
                error={Boolean(companyErrors.companyEmail)}
                helperText={companyErrors.companyEmail || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={companyDetails.companyAddress}
                onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyAddress: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={companyCountries}
                getOptionLabel={(option) => option?.name || ''}
                filterOptions={countryFilterOptions}
                slotProps={companyDrawerOverlays.autocompleteSlotProps}
                value={selectedCompanyCountryObj}
                onChange={(event, newValue) => {
                  const countryName = newValue?.name || '';
                  setCompanyDetails((prev) => ({
                    ...prev,
                    companyCountry: countryName,
                    companyState: '',
                    companyCity: '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Country" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={companyStates}
                getOptionLabel={(option) => option?.name || ''}
                filterOptions={stateFilterOptions}
                slotProps={companyDrawerOverlays.autocompleteSlotProps}
                value={selectedCompanyStateObj}
                onChange={(event, newValue) => {
                  const stateName = newValue?.name || '';
                  setCompanyDetails((prev) => ({
                    ...prev,
                    companyState: stateName,
                    companyCity: '',
                  }));
                }}
                disabled={!selectedCompanyCountryObj}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="State" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={companyCities}
                getOptionLabel={(option) => option || ''}
                filterOptions={cityFilterOptions}
                slotProps={companyDrawerOverlays.autocompleteSlotProps}
                value={companyCities.find((city) => city === companyDetails.companyCity) || null}
                onChange={(event, newValue) => {
                  setCompanyDetails((prev) => ({
                    ...prev,
                    companyCity: newValue || '',
                  }));
                }}
                disabled={!selectedCompanyStateObj}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="City" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pincode"
                value={companyDetails.companyPincode}
                onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyPincode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 0.5 }}>
                Optional: add company details for corporate invoices and booking source &quot;Corporate&quot;.
                Skip this for a normal walk-in; saved profile data loads here only when you open this form.
              </Alert>
            </Grid>
          </Grid>
          </Box>

          {/* Footer */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, ...getBookingDrawerFooterSx(isDark) }}>
            <Button
              onClick={() => setCompanyDialogOpen(false)}
              variant="outlined"
              fullWidth
              sx={getBookingDrawerOutlinedButtonSx(isDark)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompanyDetailsSave}
              variant="contained"
              fullWidth
              sx={getBookingDrawerPrimaryButtonSx(isDark)}
            >
              Add Company Detail
            </Button>
          </Box>
        </Box>
      </Drawer>

      <DashboardHousekeepingDrawer
        cleaningId={housekeepingCleaningId}
        open={Boolean(housekeepingCleaningId)}
        onClose={handleHousekeepingClose}
        onSuccess={handleHousekeepingSuccess}
        onError={handleHousekeepingError}
        themeMode={currentTheme}
      />

      {housekeepingError ? (
        <Alert
          severity="error"
          onClose={() => setHousekeepingError('')}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: (theme) => theme.zIndex.snackbar,
            maxWidth: 480,
            width: 'calc(100% - 32px)',
          }}
        >
          {housekeepingError}
        </Alert>
      ) : null}
      
      </Box>
    </LocalizationProvider>
  );
};

export default WalkInForm;

